import { AuthGuard } from '@/components/auth/auth-guard';
import { JiraIcon, LinearIcon, PeopleForceIcon } from '@/components/icons';
import { AppLayout } from '@/components/layout';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { useExtensions } from '@/hooks/use-extensions';
import { useAuthStore } from '@/stores/auth';
import { useExtensionsStore } from '@/stores/extensions';
import { getPeopleForceStatus } from '@/lib/peopleforce';
import { getJiraStatus } from '@/lib/jira-import';
import { getClockifyStatus } from '@/lib/clockify-import';
import { CompanyInfoForm } from '@/features/settings/company-info-form';
import { cn, normalizeRoutePath } from '@/lib/utils';
import type { ExtensionStatus } from '@hygge/shared';
import { useQuery } from '@tanstack/react-query';
import {
  Link,
  Outlet,
  createFileRoute,
  redirect,
  useLocation,
  useNavigate,
} from '@tanstack/react-router';
import { hasPlaceholderPermission, hasPermission, isSuperAdmin } from '@/lib/permissions';
import {
  Bell,
  Bot,
  Briefcase,
  Building2,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Clock,
  Columns3,
  Download,
  FileText,
  KeyRound,
  LayoutDashboard,
  Linkedin,
  Loader2,
  Megaphone,
  Monitor,
  Paintbrush,
  Shield,
  Sparkles,
  Terminal,
  Timer,
  Upload,
  Users,
} from 'lucide-react';
import { useMemo } from 'react';
import { NotificationsPage } from './settings.notifications';
import { ImportPage } from './settings.import';
import { ExportPage } from './settings.export';
import { ChangelogPage } from './settings.changelog';
import { CustomizationPage } from './settings.customization';
import { AiSettingsPage } from './settings.ai';
import { EmploymentTypesPage } from './settings.employment-types';
import { PositionsPage } from './settings.positions';
import { AssetTypesPage } from './settings.asset-types';
import { AccessTypesPage } from './settings.access-types';
import { AbsenceTypesPage } from './settings.absence-types';
import { RolesPermissionsPage } from './settings.roles';
import { DashboardConfiguratorPage } from '@/features/dashboard/dashboard-configurator';
import { PreboardingFormsList } from '@/features/preboarding-forms/forms-list';

const routePath = '/settings' as const;
export const Route = createFileRoute(routePath)({
  validateSearch: (search: Record<string, unknown>): { tab?: string } => ({
    tab: typeof search.tab === 'string' ? search.tab : undefined,
  }),
  beforeLoad: async () => {
    const { isAuthenticated, isInitialized } = useAuthStore.getState();
    if (!isInitialized) return;
    if (!isAuthenticated) throw redirect({ to: '/login' });

    const user = useAuthStore.getState().user;
    if (hasPlaceholderPermission(user)) throw redirect({ to: '/placeholder' });
  },
  component: SettingsPage,
});

type SettingsCategory =
  | 'general'
  | 'configurations'
  | 'access_control'
  | 'absences'
  | 'forms'
  | 'ai'
  | 'integrations';

interface SettingItem {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  href: string;
  category: SettingsCategory;
  /**
   * Single required permission to see this item (undefined = visible to all).
   * Use this for the common case. For "visible if the user holds ANY of several
   * permissions" use `permissions` instead — set one or the other, not both
   * (`permissions` takes precedence in the visibility filter).
   */
  permission?: string;
  /** Any-of: item is visible if the user holds at least one of these permissions. */
  permissions?: string[];
  /** When true, only super-admins see this item, regardless of permission codes. */
  superAdminOnly?: boolean;
  /**
   * Extension id this section belongs to. The section is hidden while that
   * extension is disabled, however many permissions the reader holds — the
   * switches on a disabled feature's screen drive nothing.
   */
  extension?: string;
}

const settingsItems: SettingItem[] = [
  // GENERAL
  {
    id: 'general',
    title: 'Company Information',
    description: 'Company name, logo, timezone, and regional settings',
    icon: Building2,
    href: '/settings/general',
    category: 'general',
    permission: 'settings.general.manage',
  },
  {
    id: 'notifications',
    title: 'Notifications',
    description: 'Email and system alerts configuration',
    icon: Bell,
    href: '/settings/notifications',
    category: 'general',
    permission: 'settings.general.manage',
  },
  {
    id: 'import',
    title: 'Import',
    description: 'Import data from external sources',
    icon: Upload,
    href: '/settings/import',
    category: 'general',
    permission: 'settings.general.manage',
  },
  {
    id: 'export',
    title: 'Export',
    description: 'Export data to various formats',
    icon: Download,
    href: '/settings/export',
    category: 'general',
    permission: 'settings.general.manage',
  },
  {
    id: 'changelog',
    title: "What's New",
    description: 'View recent updates and release notes',
    icon: Megaphone,
    href: '/settings/changelog',
    category: 'general',
    // No permission — visible to everyone
  },
  {
    id: 'customization',
    title: 'Appearance',
    description: 'Seasonal effects, themes, and visual customization',
    icon: Paintbrush,
    href: '/settings/customization',
    category: 'general',
    permission: 'settings.general.manage',
  },
  // CONFIGURATIONS
  {
    id: 'employment-types',
    title: 'Employment Types',
    description: 'Manage employment types like Full-time, Part-time, Contractor',
    icon: Users,
    href: '/settings/employment-types',
    category: 'configurations',
    permission: 'settings.general.manage',
  },
  {
    id: 'positions',
    title: 'Positions',
    description: 'Manage job position titles, categories and departments',
    icon: Briefcase,
    href: '/settings/positions',
    category: 'configurations',
    permission: 'organization.positions.manage',
  },
  {
    id: 'asset-types',
    title: 'Asset Types',
    description: 'Configure asset categories like Laptops, Monitors, Phones',
    icon: Monitor,
    href: '/settings/asset-types',
    category: 'configurations',
    permission: 'assets.types.manage',
  },
  {
    id: 'access-types',
    title: 'Access Types',
    description: 'Configure system access categories like GitHub, Slack, AWS',
    icon: KeyRound,
    href: '/settings/access-types',
    category: 'configurations',
    permission: 'access.types.manage',
  },
  // ACCESS CONTROL
  {
    id: 'roles',
    title: 'Roles & Permissions',
    description: 'Manage user roles and configure granular access permissions',
    icon: Shield,
    href: '/settings/roles',
    category: 'access_control',
    // Read by either code, as the server reads the matrix. [HW-1062]
    permissions: ['settings.roles.view', 'settings.roles.manage'],
  },
  {
    id: 'dashboard-configurator',
    extension: 'home-dashboard',
    title: 'Dashboard Configurator',
    description: "Choose which widgets each role sees on their home dashboard, and in what order",
    icon: LayoutDashboard,
    href: '/settings/dashboard-configurator',
    category: 'access_control',
    superAdminOnly: true,
  },
  // ABSENCES
  {
    id: 'absence-types',
    title: 'Absence Types',
    description: 'Configure leave types, policies, and accrual rules',
    icon: Calendar,
    href: '/settings/absence-types',
    category: 'absences',
    permission: 'absences.types.manage',
  },
  // HR FORMS
  {
    id: 'preboarding-forms',
    title: 'Pre-boarding Forms',
    description: 'Build forms to collect documents and data from new hires before they join',
    icon: FileText,
    href: '/settings/preboarding-forms',
    category: 'forms',
    permission: 'forms.view',
  },
  // AI
  {
    id: 'ai',
    title: 'Intelligence Configuration',
    description: 'Manage intelligence providers, model assignments per module, and usage analytics',
    icon: Bot,
    href: '/settings/ai',
    category: 'ai',
    permission: 'settings.general.manage',
    extension: 'ai-assistant',
  },
  // INTEGRATIONS
  {
    id: 'integrations',
    title: 'Integrations',
    description: 'Connect external tools, manage extensions and data sync',
    icon: Columns3,
    href: '/settings/integrations',
    category: 'integrations',
    // Admins manage all integrations; LinkedIn-only recruiters get in to see just that card.
    permissions: ['settings.general.manage', 'extensions.linkedin.use'],
  },
];

const categoryLabels: Record<SettingsCategory, string> = {
  general: 'GENERAL',
  configurations: 'CONFIGURATIONS',
  access_control: 'ACCESS CONTROL',
  absences: 'ABSENCES',
  forms: 'HR FORMS',
  ai: 'INTELLIGENCE',
  integrations: 'INTEGRATIONS',
};

// Route content components mounted inline per `?tab`. `general` (company form)
// and `integrations` render their own inline content in the page below.
const SECTION_COMPONENTS: Record<string, React.ComponentType> = {
  notifications: NotificationsPage,
  import: ImportPage,
  export: ExportPage,
  changelog: ChangelogPage,
  customization: CustomizationPage,
  ai: AiSettingsPage,
  'employment-types': EmploymentTypesPage,
  positions: PositionsPage,
  'asset-types': AssetTypesPage,
  'access-types': AccessTypesPage,
  'absence-types': AbsenceTypesPage,
  roles: RolesPermissionsPage,
  'dashboard-configurator': DashboardConfiguratorPage,
  'preboarding-forms': PreboardingFormsList,
};

function SettingsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { tab: activeTabParam } = Route.useSearch();
  const user = useAuthStore((s) => s.user);

  const isExtensionEnabled = useExtensionsStore((s) => s.isExtensionEnabled);
  const enabledExtensions = useExtensionsStore((s) => s.enabledExtensions);

  // Sections visible to this user (permission- and extension-gated).
  // `permissions` is any-of.
  const visibleItems = useMemo(
    () =>
      settingsItems.filter((item) => {
        if (item.extension && !isExtensionEnabled(item.extension)) return false;
        if (item.superAdminOnly && !isSuperAdmin(user)) return false;
        if (item.permissions) return item.permissions.some((p) => hasPermission(user, p));
        return !item.permission || hasPermission(user, item.permission);
      }),
    // `enabledExtensions` is the value the predicate actually reads; the getter
    // identity is stable, so it alone would never re-run this.
    [user, isExtensionEnabled, enabledExtensions]
  );

  // Active section comes from ?tab (fallback to the first visible section).
  const activeId =
    visibleItems.find((i) => i.id === activeTabParam)?.id ?? visibleItems[0]?.id ?? 'general';
  const activeItem = visibleItems.find((i) => i.id === activeId);
  const activeCategory: SettingsCategory = activeItem?.category ?? 'general';

  // Categories that have at least one visible section, in declared order.
  const visibleCategories = useMemo(() => {
    const cats = new Set(visibleItems.map((i) => i.category));
    return Object.entries(categoryLabels).filter(([key]) => cats.has(key as SettingsCategory));
  }, [visibleItems]);

  // Sections within the active category (drives the secondary pill row).
  const categorySections = useMemo(
    () => visibleItems.filter((i) => i.category === activeCategory),
    [visibleItems, activeCategory]
  );

  // A drill-in route (e.g. integration detail, absence-type configure) is any
  // path deeper than /settings — render it via <Outlet/> under the tabs. A typed
  // `/settings/` keeps its trailing slash here, so compare on the trimmed path.
  const isDrillIn = normalizeRoutePath(location.pathname) !== normalizeRoutePath(routePath);
  const drillInParent = normalizeRoutePath(location.pathname).split('/')[2] || 'general';

  const selectTab = (id: string) => navigate({ to: '/settings', search: { tab: id } });
  const selectCategory = (cat: SettingsCategory) => {
    const first = visibleItems.find((i) => i.category === cat);
    if (first) selectTab(first.id);
  };

  const ActiveComponent = SECTION_COMPONENTS[activeId];

  return (
    <AuthGuard>
      <AppLayout>
        {/* Breadcrumb + tabs — one sticky block so the rows never overlap */}
        <div className="sticky top-14 z-30 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="flex h-11 items-center px-6">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>Home</span>
              <span>/</span>
              <span className="font-medium text-foreground">Settings</span>
            </div>
          </div>
          <nav className="flex gap-6 overflow-x-auto px-6" aria-label="Settings categories">
            {visibleCategories.map(([key, label]) => {
              const isActive = key === activeCategory;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => selectCategory(key as SettingsCategory)}
                  className={cn(
                    'relative whitespace-nowrap pb-3 text-sm font-medium transition-colors cursor-pointer',
                    isActive ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  {label}
                  {isActive && (
                    <span className="absolute -bottom-px left-0 right-0 h-0.5 rounded-t-full bg-hygge" />
                  )}
                </button>
              );
            })}
          </nav>
          {categorySections.length > 1 && (
            <div className="flex gap-1.5 overflow-x-auto px-6 py-2.5">
              {categorySections.map((s) => {
                const isActive = s.id === activeId;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => selectTab(s.id)}
                    className={cn(
                      'whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors cursor-pointer',
                      isActive
                        ? 'bg-cta text-cta-foreground'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    )}
                  >
                    {s.title}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Content */}
        {isDrillIn ? (
          <div>
            <div className="px-6 pt-4">
              <button
                type="button"
                onClick={() => selectTab(drillInParent)}
                className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-hygge transition-colors cursor-pointer"
              >
                <ChevronLeft className="h-4 w-4" />
                Back
              </button>
            </div>
            <Outlet />
          </div>
        ) : activeId === 'general' ? (
          <CompanyInfoForm />
        ) : activeId === 'integrations' ? (
          <div className="mx-auto max-w-5xl space-y-6 p-6">
            <IntegrationsTabContent />
          </div>
        ) : ActiveComponent ? (
          <ActiveComponent />
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <p className="text-sm text-muted-foreground">No settings available</p>
          </div>
        )}
      </AppLayout>
    </AuthGuard>
  );
}

// --- Integrations Tab (inline content) ---

type IntBadgeVariant = 'connected' | 'enabled' | 'disabled' | 'coming-soon';

interface IntCard {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  iconStyle: string;
  badge: IntBadgeVariant;
  version?: string;
  href?: string;
  comingSoon?: boolean;
  /** When set, the card is only shown to users who hold this permission. */
  permission?: string;
}

const intBadgeConfig: Record<IntBadgeVariant, { label: string; className: string; dot?: boolean }> =
  {
    connected: {
      label: 'Connected',
      className: 'text-green-600 border-green-600/30 bg-green-600/5',
      dot: true,
    },
    enabled: {
      label: 'Enabled',
      className: 'text-green-600 border-green-600/30 bg-green-600/5',
      dot: true,
    },
    disabled: { label: 'Disabled', className: 'text-muted-foreground border-border bg-muted/50' },
    'coming-soon': {
      label: 'Coming Soon',
      className: 'text-blue-500 border-blue-500/30 bg-blue-500/5',
    },
  };

const extIconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  kanban: Columns3,
  clock: Clock,
  sparkles: Sparkles,
};

const extIconStyleMap: Record<string, string> = {
  'task-manager': 'bg-hygge/10 text-hygge',
  'time-tracker': 'bg-amber-500/10 text-amber-600',
  'ai-assistant': 'bg-violet-500/10 text-violet-600',
};

// LinkedIn Parser is a Chrome extension installed by recruiters into their own
// browser — not a HyggeOS-controlled module like Task Manager / Time Tracker, so it
// doesn't live in the `extensions` table. The card is hardcoded; only the
// downloadable build is server-backed (LinkedinParserRelease).
const linkedinParserCard: IntCard = {
  id: 'linkedin-parser',
  title: 'LinkedIn Parser',
  description:
    'Chrome extension that parses LinkedIn profile pages and pushes candidates into HyggeOS',
  icon: Linkedin,
  iconStyle: 'bg-blue-500/10 text-blue-600',
  badge: 'enabled',
  href: '/settings/integrations/linkedin-parser',
  permission: 'extensions.linkedin.use',
};

/** The permission every Clockify import route requires. Named once so the card and the query agree. */
// The READ half: the card and the status probe only need to see the import.
// Anyone who can run it also holds this — 20260826170000 grants view to every
// role with `time.import` — so gating the card on view alone excludes nobody.
const CLOCKIFY_PERMISSION = 'time.import.view';

function statusToBadge(status: ExtensionStatus): IntBadgeVariant {
  return status === 'enabled' ? 'enabled' : 'disabled';
}

function IntegrationsTabContent() {
  const user = useAuthStore((s) => s.user);
  // [HW-1014] The status route is admin-only now. Asked only by those the
  // PeopleForce card is shown to (the `gate()` below), so a LinkedIn-only
  // recruiter's Settings page does not fire a request the API will refuse.
  const { data: pfStatus } = useQuery({
    queryKey: ['peopleforce', 'status'],
    queryFn: getPeopleForceStatus,
    retry: false,
    enabled: hasPermission(user, 'settings.general.manage'),
  });
  const { data: jiraStatus } = useQuery({ queryKey: ['jira', 'status'], queryFn: getJiraStatus, retry: false });
  const { data: extensions, isLoading: extensionsLoading } = useExtensions();
  // Gated on the same permission the card is, so nobody else's Settings page
  // fires a request the API will refuse.
  const canImportTime = hasPermission(user, CLOCKIFY_PERMISSION);
  const { data: clockifyStatus } = useQuery({
    queryKey: ['clockify', 'status'],
    queryFn: getClockifyStatus,
    retry: false,
    enabled: canImportTime,
  });

  const pfConnected = pfStatus?.configured && pfStatus?.success;

  const pfCard: IntCard = {
    id: 'peopleforce',
    title: 'PeopleForce Sync',
    description: 'Sync employees, avatars, calendar, compensations, and projects from PeopleForce',
    icon: PeopleForceIcon,
    iconStyle: 'bg-green-500/10 text-green-600',
    badge: pfConnected ? 'connected' : 'disabled',
    href: '/settings/integrations/peopleforce',
  };

  /**
   * The one-time Clockify migration. Gated on `time.import` rather than the
   * blanket settings permission: what it writes is timesheet data for other
   * people, so the Time Tracker's own permission decides who may run it — and
   * the card must not offer a page every route of which would answer 403.
   */
  const clockifyCard: IntCard = {
    id: 'clockify-import',
    title: 'Clockify Import',
    description:
      'One-time migration of Clockify time entries into the Time Tracker — mapped, previewed, and reversible',
    icon: Timer,
    iconStyle: 'bg-sky-500/10 text-sky-600',
    badge: clockifyStatus?.connected ? 'connected' : 'disabled',
    href: '/settings/integrations/clockify',
    permission: CLOCKIFY_PERMISSION,
  };

  const jiraCard: IntCard = {
    id: 'jira-import',
    title: 'Jira Import',
    description: 'Import projects and tickets from Jira Cloud — re-runnable, pulls updates each run',
    icon: JiraIcon,
    iconStyle: 'bg-blue-500/10',
    badge: jiraStatus?.connected ? 'connected' : 'disabled',
    href: '/settings/integrations/jira',
  };

  const extensionCards: IntCard[] = [
    ...(extensions ?? []).map((ext) => ({
      id: ext.id,
      title: ext.name,
      description: ext.description,
      icon: extIconMap[ext.icon] ?? Columns3,
      iconStyle: extIconStyleMap[ext.id] ?? 'bg-hygge/10 text-hygge',
      badge: statusToBadge(ext.status),
      version: `v${ext.version}`,
      href: `/settings/integrations/${ext.id}`,
    })),
    linkedinParserCard,
  ];

  const comingSoonCards: IntCard[] = [
    {
      id: 'linear-import',
      title: 'Linear Import',
      description: 'Import projects and issues from Linear workspaces via OAuth',
      icon: LinearIcon,
      iconStyle: 'bg-blue-500/10',
      badge: 'coming-soon',
      version: 'Planned',
      comingSoon: true,
    },
    {
      id: 'mcp-access',
      title: 'MCP Access',
      description: 'API tokens and configuration for AI tool integrations (Cursor, Claude Code)',
      icon: Terminal,
      iconStyle: 'bg-blue-500/10 text-blue-500',
      badge: 'coming-soon',
      version: 'Planned',
      comingSoon: true,
    },
  ];

  // Every integration except LinkedIn is admin-managed: gate it behind
  // settings.general.manage so a LinkedIn-only recruiter sees ONLY the LinkedIn card.
  // (LinkedIn keeps its own extensions.linkedin.use; cards already carrying a
  // permission are left as-is.)
  const ADMIN = 'settings.general.manage';
  const gate = (card: IntCard): IntCard => (card.permission ? card : { ...card, permission: ADMIN });

  const sections: { label: string; cards: IntCard[]; loading?: boolean }[] = [
    { label: 'Data Sync', cards: [gate(pfCard), gate(jiraCard), gate(clockifyCard)] },
    { label: 'Extensions', cards: extensionCards.map(gate), loading: extensionsLoading },
    { label: 'Import & Intelligence Tools', cards: comingSoonCards.map(gate) },
  ];

  // Apply per-card permission gating, then drop sections that end up empty
  // (e.g. a LinkedIn-only recruiter sees just the Extensions section).
  const visibleSections = sections
    .map((section) => ({
      ...section,
      cards: section.cards.filter((card) => !card.permission || hasPermission(user, card.permission)),
    }))
    .filter((section) => section.loading || section.cards.length > 0);

  return (
    <div className="space-y-6">
      {visibleSections.map((section) => (
        <div key={section.label}>
          <h2 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-3 pb-2 border-b border-border">
            {section.label}
          </h2>
          {section.loading ? (
            <div className="flex items-center gap-2 py-8 justify-center text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Loading extensions...</span>
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {section.cards.map((card) => (
                <IntCardComponent key={card.id} card={card} />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function IntCardComponent({ card }: { card: IntCard }) {
  const config = intBadgeConfig[card.badge];
  const Icon = card.icon;

  const content = (
    <Card
      className={cn(
        'group transition-all h-full',
        card.comingSoon
          ? 'opacity-60 cursor-default'
          : 'cursor-pointer hover:shadow-md hover:border-hygge/20'
      )}
    >
      <CardContent className="flex flex-col gap-3.5 p-5">
        <div className="flex items-start justify-between">
          <div
            className={cn(
              'flex h-10 w-10 items-center justify-center rounded-full',
              card.iconStyle
            )}
          >
            <Icon className="h-5 w-5" />
          </div>
          <Badge
            variant="outline"
            className={cn('text-[11px] px-2 py-0.5 font-medium', config.className)}
          >
            {config.dot && (
              <span className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-current" />
            )}
            {config.label}
          </Badge>
        </div>
        <div>
          <h3 className="text-sm font-semibold">{card.title}</h3>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{card.description}</p>
        </div>
        <div className="flex items-center justify-between mt-auto pt-1">
          {card.version ? (
            <span className="text-[11px] text-muted-foreground font-mono">{card.version}</span>
          ) : (
            <span />
          )}
          {!card.comingSoon && (
            <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-hygge" />
          )}
        </div>
      </CardContent>
    </Card>
  );

  if (card.comingSoon || !card.href) return content;

  return (
    <Link to={card.href} className="no-underline">
      {content}
    </Link>
  );
}
